// pages/admin/SubscriptionList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import {
  MdSearch, MdCardMembership, MdPerson, MdPhone, MdEmail,
  MdClose, MdStar, MdAttachMoney,
} from 'react-icons/md';

export default function SubscriptionList() {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;

  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search.trim()) params.search = search.trim();
      if (planFilter) params.plan = planFilter;
      if (statusFilter) params.status = statusFilter;

      const res = await axios.get(
        `${base_url}/api/v1/subscription/admin/list`,
        { params }
      );
      if (res.data.success) {
        setSubs(res.data.data?.data || []);
        setStats(res.data.data?.stats || null);
        setPagination(
          res.data.data?.pagination || { total: 0, totalPages: 0 }
        );
      }
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [base_url, page, search, planFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (page !== 1) setPage(1);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this subscription? User will lose access immediately.')) return;
    setActionLoading((p) => ({ ...p, [id]: true }));
    try {
      await axios.post(
        `${base_url}/api/v1/subscription/admin/${id}/cancel`
      );
      setSubs((prev) =>
        prev.map((s) => (s.id === id ? { ...s, status: 'cancelled' } : s))
      );
      load();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed');
    } finally {
      setActionLoading((p) => ({ ...p, [id]: false }));
    }
  };

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : '-';

  const formatDateTime = (d) =>
    d
      ? new Date(d).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '-';

  const planBadge = (p) => {
    if (p === 'premium') return 'warning';
    if (p === 'basic') return 'primary';
    return 'secondary';
  };

  const statusBadge = (s) => {
    const map = { active: 'success', cancelled: 'danger', pending: 'secondary' };
    return map[s] || 'secondary';
  };

  const paymentBadge = (s) => {
    const map = { success: 'success', failed: 'danger', pending: 'warning' };
    return map[s] || 'secondary';
  };

  return (
    <div className="page-wrapper">
      <div className="container-fluid mt-3">
        {/* Stats cards */}
        {stats && (
          <div className="row g-3 mb-3">
            <div className="col-md-3 col-sm-6">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body">
                  <small className="text-muted d-block mb-1">Active Subscriptions</small>
                  <h3 className="fw-bold mb-0 text-success">{stats.active_count}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body">
                  <small className="text-muted d-block mb-1">Basic / Premium</small>
                  <h3 className="fw-bold mb-0">
                    <span className="text-primary">{stats.basic_count}</span>
                    <span className="text-muted mx-1">/</span>
                    <span className="text-warning">{stats.premium_count}</span>
                  </h3>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body">
                  <small className="text-muted d-block mb-1">Total Revenue</small>
                  <h3 className="fw-bold mb-0 text-success">
                    ₹{Number(stats.total_revenue || 0).toLocaleString('en-IN')}
                  </h3>
                </div>
              </div>
            </div>
            <div className="col-md-3 col-sm-6">
              <div className="card shadow-sm border-0 h-100">
                <div className="card-body">
                  <small className="text-muted d-block mb-1">Last 30 Days</small>
                  <h3 className="fw-bold mb-0 text-primary">
                    ₹{Number(stats.revenue_30d || 0).toLocaleString('en-IN')}
                  </h3>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="card shadow border-0">
          {/* Header */}
          <div className="card-header bg-white">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <h4 className="fw-bold mb-0">
                <MdCardMembership className="me-2 text-primary" />
                Subscriptions
                <span className="badge bg-primary ms-2">{pagination.total}</span>
              </h4>
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text"><MdSearch /></span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search user, email, phone, order id..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>

              <div className="col-md-3">
                <select
                  className="form-select"
                  value={planFilter}
                  onChange={(e) => { setPlanFilter(e.target.value); setPage(1); }}
                >
                  <option value="">All Plans</option>
                  <option value="basic">Basic (₹99)</option>
                  <option value="premium">Premium (₹199)</option>
                </select>
              </div>

              <div className="col-md-3">
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 60 }}>#</th>
                    <th>User</th>
                    <th>Contact</th>
                    <th>Plan</th>
                    <th style={{ width: 80 }}>Price</th>
                    <th>Payment</th>
                    <th>Status</th>
                    <th>Started</th>
                    <th>Expires</th>
                    <th style={{ width: 110 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(6)].map((_, i) => (
                      <tr key={i}>
                        {[...Array(10)].map((__, j) => (
                          <td key={j}><Skeleton /></td>
                        ))}
                      </tr>
                    ))
                  ) : subs.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center py-5 text-muted">
                        <MdCardMembership size={40} />
                        <div className="mt-2">No subscriptions yet</div>
                      </td>
                    </tr>
                  ) : (
                    subs.map((s, idx) => (
                      <tr key={s.id}>
                        <td>{(page - 1) * limit + idx + 1}</td>

                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div
                              style={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                background: '#01457B',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 700,
                                flexShrink: 0,
                              }}
                            >
                              {(s.user_name || 'U').charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <strong>{s.user_name || 'Unknown'}</strong>
                              <div className="small text-muted">ID #{s.user_id}</div>
                            </div>
                          </div>
                        </td>

                        <td>
                          {s.user_email && (
                            <div className="small text-truncate" style={{ maxWidth: 200 }}>
                              <MdEmail /> {s.user_email}
                            </div>
                          )}
                          {s.user_phone && (
                            <div className="small text-muted">
                              <MdPhone /> {s.user_phone}
                            </div>
                          )}
                        </td>

                        <td>
                          <span className={`badge bg-${planBadge(s.plan_name)} text-uppercase`}>
                            {s.plan_name}
                          </span>
                          {s.order_id && (
                            <div className="small text-muted text-truncate" style={{ maxWidth: 180 }}>
                              {s.order_id}
                            </div>
                          )}
                        </td>

                        <td>
                          <strong>₹{s.price}</strong>
                        </td>

                        <td>
                          <span className={`badge bg-${paymentBadge(s.payment_status)}`}>
                            {s.payment_status || 'pending'}
                          </span>
                        </td>

                        <td>
                          {s.is_currently_active ? (
                            <span className="badge bg-success">Active</span>
                          ) : s.status === 'active' ? (
                            <span className="badge bg-warning text-dark">Expired</span>
                          ) : (
                            <span className={`badge bg-${statusBadge(s.status)}`}>
                              {s.status}
                            </span>
                          )}
                        </td>

                        <td className="small">{formatDate(s.started_at)}</td>

                        <td className="small">
                          {s.expires_at ? formatDate(s.expires_at) : '—'}
                        </td>

                        <td>
                          {s.status === 'active' && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              disabled={actionLoading[s.id]}
                              onClick={() => handleCancel(s.id)}
                              title="Cancel subscription"
                            >
                              <MdClose /> Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted">
                  Showing {Math.min((page - 1) * limit + 1, pagination.total)} -{' '}
                  {Math.min(page * limit, pagination.total)} of {pagination.total}
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    Page <strong>{page}</strong> of <strong>{pagination.totalPages}</strong>
                  </span>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}